import { context, utils } from '@ad-engine/core';

interface PriceFloorsConfig {
	floors: {
		enforcement?: {
			floorDeals?: boolean;
		};
		floorMin?: number;
		data: {
			skipRate?: number;
			currency?: string;
			schema?: {
				fields: string[];
			};
			values: BidderPriceFloors;
		};
		additionalSchemaFields?: {
			bidderType: (bidRequest: BidRequest) => string | undefined;
		};
	};
}

interface BidRequest {
	bidder: string;
	mediaTypes: object;
}

interface BidderPriceFloors {
	[key: string]: number;
}

type SupportedIdentityPartners = 'id5' | 'liveintent' | 'yahoo';

const logGroup = 'prebid-price-floors-mechanism';

class PriceFloorsMechanism {
	getPriceFloors(): PriceFloorsConfig | undefined {
		if (!this.isEnabled()) {
			utils.logger(logGroup, 'disabled');
			return;
		}

		utils.logger(logGroup, 'enabled');

		let config;

		if (context.get('bidders.prebid.priceFloors.identityBasedConfig')) {
			config = this.getConfigBasedOnIdentityPartners();
		} else if (context.get('bidders.prebid.priceFloors.config')) {
			config = this.getSimpleConfig();
		}

		if (!config || Object.keys(config).length === 0) {
			utils.logger(logGroup, 'something went wrong when parsing config');
			return;
		}

		utils.logger(logGroup, 'config before mapping', config);

		const mappedConfig = this.mapConfigKeysToBannerMediaType(config);

		utils.logger(logGroup, 'config after mapping', mappedConfig);

		return {
			floors: {
				enforcement: {
					floorDeals: true,
				},
				data: {
					skipRate: 0,
					currency: 'USD',
					schema: {
						fields: ['mediaType', 'bidderType'],
					},
					values: mappedConfig,
				},
				additionalSchemaFields: {
					bidderType: this.bidderType,
				},
			},
		};
	}

	isEnabled(): boolean {
		return context.get('bidders.prebid.priceFloors.enabled');
	}

	bidderType(bidRequest: BidRequest): string | undefined {
		return bidRequest?.bidder;
	}

	mapConfigKeysToBannerMediaType(config: BidderPriceFloors): BidderPriceFloors {
		// For now, we only support banner media type
		const mediaType = 'banner';
		const mappedConfig = {};

		for (const [key, value] of Object.entries(config)) {
			const combinedKey = mediaType + '|' + key;
			mappedConfig[combinedKey] = value;
		}

		return mappedConfig;
	}

	getSimpleConfig(): BidderPriceFloors | undefined {
		utils.logger(logGroup, 'old config strategy');

		const config = context.get('bidders.prebid.priceFloors.config');

		utils.logger(logGroup, 'config provided from ICBM variable', config);

		if (!this.isConfigCorrect(config)) {
			return;
		}

		return config;
	}

	getConfigBasedOnIdentityPartners(): BidderPriceFloors | undefined {
		utils.logger(logGroup, 'identity partners based config strategy');

		const icbmConfig: BidderPriceFloors = context.get(
			'bidders.prebid.priceFloors.identityBasedConfig',
		);

		utils.logger(logGroup, 'config provided from ICBM variable', icbmConfig);

		if (!this.isConfigCorrect(icbmConfig)) {
			return;
		}

		const enabledPartners = this.getEnabledIdentityPartners();
		const keyCombinedOfIdentityPartners = enabledPartners.join('-');

		utils.logger(logGroup, 'enabled partners', enabledPartners);

		const config = {};

		for (const [key, value] of Object.entries(icbmConfig)) {
			const priceFloor: string = value[keyCombinedOfIdentityPartners];

			if (priceFloor && typeof parseFloat(priceFloor) === 'number') {
				config[key] = parseFloat(priceFloor);
			}
		}

		return config;
	}

	isConfigCorrect(config: BidderPriceFloors): boolean {
		if (!config || Object.keys(config).length === 0) {
			utils.logger(logGroup, 'no config provided');
			return false;
		}

		if (typeof config !== 'object') {
			utils.logger(logGroup, 'invalid config provided');
			return false;
		}

		return true;
	}

	// This method supports only id5, liveintent and yahoo
	// The returned enabledPartners array should be in alphabetical order
	// To support more partners, this method should be updated
	getEnabledIdentityPartners(): SupportedIdentityPartners[] {
		const enabledPartners: SupportedIdentityPartners[] = [];

		if (context.get('bidders.prebid.id5')) {
			enabledPartners.push('id5');
		}

		if (context.get('bidders.liveIntentConnectedId.enabled')) {
			enabledPartners.push('liveintent');
		}

		if (context.get('bidders.prebid.yahooConnectId.enabled')) {
			enabledPartners.push('yahoo');
		}

		enabledPartners.sort();

		return enabledPartners;
	}
}

export const priceFloorsMechanism = new PriceFloorsMechanism();
